import {
  PreferencesGetters,
  PreferencesActions,
  PreferencesMutations
} from '@/app/features/preferences/preferences.keys';
import { preferencesService } from '@/app/features/preferences/preferences.service';
import { TolErrorsActions } from '@/app/features/tol-errors/tol-errors.keys';
import i18n from '@/i18n';
import readable from '../../../filters/readable.filter';
import underscorify from '../../../filters/underscorify.filter';

export const getInitialState = () => {
  return {
    /**
     * The preference object retrieved from the database
     * has these fields:
     *   id
     *   userId
     *   objectType
     *   action
     *   inApp
     *   email
     */
    newPreferences: [],
    preferences: [],
    tolActions: {
      'Time Sheet': [
        { label: 'Saved' },
        { label: 'Submitted' },
        { label: 'Approved' },
        { label: 'Rejected' }
      ],
      'Expense Report': [
        { label: 'Saved' },
        { label: 'Submitted' },
        { label: 'Approved' },
        { label: 'Rejected' }
      ],
      Assignment: [
        { label: 'Saved' },
        { label: 'Submitted' },
        { label: 'Approved' },
        { label: 'Rejected' }
      ]
    },
    tolObjects: [{ label: 'Time Sheet' }, { label: 'Expense Report' }, { label: 'Assignment' }]
  };
};

export const preferencesStore = {
  state: getInitialState(),

  getters: {
    [PreferencesGetters.TolActions]: state => param => {
      const key = underscorify(param.toUpperCase());
      const actions = state.preferences
        .filter(p => p.objectType === key)
        .map(item => {
          return { label: item.action };
        });
      return actions;
    },
    [PreferencesGetters.TolObjects]: state => {
      const objects = [...new Set(state.preferences.map(f => f.objectType))];
      return objects.map(a => {
        const readableObject = a
          ? readable(a.toLowerCase())
          : 'System Notice'; /* object type 'System Notice' not yet in database*/
        return { label: readableObject };
      });
    },
    [PreferencesGetters.Preferences]: state => {
      return state.preferences.filter(p => p.email || p.inApp);
    },
    [PreferencesGetters.NewPreferences]: state => {
      return state.newPreferences;
    },
    [PreferencesGetters.AllowedPreferences]: state => {
      return state.preferences;
    },
    [PreferencesGetters.IsPreferenceSet]: (state, getters) => params => {
      const currentPreferenceList = getters.Preferences.concat(state.newPreferences);
      for (const preference of currentPreferenceList) {
        if (
          preference.objectType &&
          readable(preference.objectType.toLowerCase()) === params.tolObject &&
          preference.action === params.action
        ) {
          return true;
        }
      }

      return false;
    }
  },

  mutations: {
    [PreferencesMutations.RetrievePreferencesSuccess]: (state, preferences) => {
      state.preferences = preferences;
    },
    [PreferencesMutations.InsertNewPreference]: (state, preference) => {
      state.newPreferences.push(preference);
    },
    [PreferencesMutations.RemoveNewPreference]: (state, preference) => {
      const updatedNewPreferences = state.newPreferences.filter(
        p => p.action !== preference.actioin && p.objectType !== preference.objectType
      );
      state.newPreferences = updatedNewPreferences;
    }
  },

  actions: {
    [PreferencesActions.RetrievePreferences]: ({ commit, dispatch }, referenceNumber = null) => {
      return preferencesService
        .retrievePreferences(referenceNumber)
        .then(preferences => {
          commit(PreferencesMutations.RetrievePreferencesSuccess, preferences);
        })
        .catch(error =>
          dispatch(TolErrorsActions.SetError, {
            error,
            message: i18n.t('preferences.errors.retrievePreferences')
          })
        );
    },
    [PreferencesActions.AddPreference]: ({ commit }, payload) => {
      commit(PreferencesMutations.InsertNewPreference, payload);
    },
    [PreferencesActions.DeletePreference]: ({ dispatch, commit }, payload) => {
      if (!payload.id) {
        commit(PreferencesMutations.RemoveNewPreference, payload);
      } else {
        /**
         * Deleting a preference means setting both options to false,
         * Preference will not show in list on UI
         */
        const updateParams = {
          id: payload.id,
          in_app: false,
          email: false,
          userId: payload.userId
        };
        return preferencesService
          .updatePreference(updateParams)
          .then(() => {
            dispatch(PreferencesActions.RetrievePreferences, payload.referenceNumber);
          })
          .catch(error =>
            dispatch(TolErrorsActions.SetError, {
              error,
              message: i18n.t('preferences.errors.updatePreference')
            })
          );
      }
    },
    [PreferencesActions.UpdatePreference]: ({ dispatch, state }, payload) => {
      /**
       * Handle difference in architecture with V3 where all preferences
       * are already in database, regardless if they are configured or not
       * Check if preference is already stored in database
       * if it is, use the id to update it, otherwise insert it
       */
      let selectedPreference;
      let action;
      if (!('branchOrDepartment' in payload)) {
        selectedPreference = state.preferences.filter(
          p => p.action === payload.action && p.objectType === payload.objectType
        )[0];
      } else {
        let branchFilter =
          payload.branchOrDepartment === 'My Departments' ? '' : payload.branchOrDepartment;
        selectedPreference = state.preferences.filter(
          p =>
            p.action === payload.action &&
            p.objectType === payload.objectType &&
            p.branch === branchFilter
        )[0];
      }
      if (selectedPreference) {
        const updateParams = {
          id: selectedPreference.id,
          in_app: payload.inApp,
          email: payload.email,
          userId: selectedPreference.userId
        };
        action = preferencesService.updatePreference(updateParams).then(() => {
          dispatch(PreferencesActions.RetrievePreferences, payload.referenceNumber);
        });
      } else {
        action = preferencesService.addPreference(payload).then(() => {
          dispatch(PreferencesActions.RetrievePreferences, payload.referenceNumber);
        });
      }
      return action.catch(error =>
        dispatch(TolErrorsActions.SetError, {
          error,
          message: i18n.t('preferences.errors.updatePreference')
        })
      );
    }
  }
};
